import { createFileRoute, Link } from '@tanstack/react-router';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { RefreshCw } from 'lucide-react';
import { Fragment, useCallback, useState } from 'react';

import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';
import { useAdminAutoOutreachBoard } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AutoCalloutBoardCard,
    type AutoCalloutBoardLane,
} from '@libraries/typescript/api_client';

const REFRESH_INTERVAL_MS = 15_000;

export const Route = createFileRoute('/_app/admin/auto-callout-board/')({
    component: AutoOutreachBoardPage,
    head: () => ({
        meta: [{ title: 'Auto-callout Board | Phoebe Admin' }],
    }),
});

function AutoOutreachBoardPage() {
    const [orgIds, setOrgIds] = useState<Array<string>>([]);
    const orgId = orgIds[0];

    const { data, isLoading, isValidating, mutate } = useAdminAutoOutreachBoard(
        { organizationId: orgId },
        { refreshIntervalMs: REFRESH_INTERVAL_MS },
    );

    const handleRefresh = useCallback(() => {
        void mutate();
    }, [mutate]);

    const handleOrgChange = useCallback((next: Array<string>) => {
        setOrgIds(next.slice(0, 1));
    }, []);

    const lanes = data?.lanes ?? [];
    const totalCards = data?.total_cards ?? 0;
    const windowHours = data?.window_hours ?? 24;
    const fetchedAt = data?.fetched_at ? new Date(data.fetched_at) : null;

    return (
        <TooltipProvider delayDuration={150}>
            <div className="flex h-full flex-1 flex-col overflow-hidden">
                <PageHeader
                    totalCards={totalCards}
                    windowHours={windowHours}
                    fetchedAt={fetchedAt}
                    onRefresh={handleRefresh}
                    isValidating={isValidating}
                    orgIds={orgIds}
                    onOrgChange={handleOrgChange}
                />
                <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">
                    {isLoading ? (
                        <BoardSkeleton />
                    ) : (
                        <div className="-mx-2 flex min-h-0 flex-1 gap-4 overflow-x-auto px-2 pb-2">
                            {lanes.map((lane) => (
                                <Lane key={lane.stage} lane={lane} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </TooltipProvider>
    );
}

function PageHeader({
    totalCards,
    windowHours,
    fetchedAt,
    onRefresh,
    isValidating,
    orgIds,
    onOrgChange,
}: {
    readonly totalCards: number;
    readonly windowHours: number;
    readonly fetchedAt: Date | null;
    readonly onRefresh: () => void;
    readonly isValidating: boolean;
    readonly orgIds: ReadonlyArray<string>;
    readonly onOrgChange: (next: Array<string>) => void;
}) {
    return (
        <header className="bg-muted/40 border-b">
            <div className="flex items-center justify-between gap-4 px-6 py-4">
                <div className="flex flex-col gap-1">
                    <h1 className="text-lg font-semibold tracking-tight">
                        Auto-callout Board
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Phoebe-drafted callouts in flight, plus anything that
                        resolved or cancelled in the last {windowHours} hours.
                    </p>
                </div>
                <div className="text-muted-foreground flex items-center gap-3 text-xs">
                    <div className="w-64">
                        <OrganizationCombobox
                            value={orgIds}
                            onValueChange={onOrgChange}
                            placeholder="All organizations"
                        />
                    </div>
                    <span className="tabular-nums">
                        {totalCards} in view
                        {fetchedAt ? (
                            <Fragment>
                                {' · '}updated{' '}
                                {formatDistanceToNowStrict(fetchedAt, {
                                    addSuffix: true,
                                })}
                            </Fragment>
                        ) : null}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onRefresh}
                        disabled={isValidating}
                    >
                        <RefreshCw
                            className={cn(isValidating && 'animate-spin')}
                        />
                        Refresh
                    </Button>
                </div>
            </div>
        </header>
    );
}

function BoardSkeleton() {
    return (
        <div className="-mx-2 flex min-h-0 flex-1 gap-4 overflow-x-auto px-2 pb-2">
            {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="flex min-w-[280px] flex-1 flex-col">
                    <Skeleton className="h-32 w-full" />
                </div>
            ))}
        </div>
    );
}

function Lane({ lane }: { readonly lane: AutoCalloutBoardLane }) {
    return (
        <Card className="flex min-w-[280px] flex-1 flex-col gap-0 overflow-hidden py-0 shadow-none">
            <CardHeader className="flex min-h-[64px] flex-col justify-start gap-1 px-4 py-3">
                <CardTitle className="flex items-center justify-between gap-2 text-[13px] font-medium tracking-wide uppercase">
                    <span className="text-muted-foreground truncate">
                        {lane.title}
                    </span>
                    <Badge variant="secondary" className="tabular-nums">
                        {lane.cards.length}
                    </Badge>
                </CardTitle>
                <CardDescription className="text-xs leading-snug normal-case">
                    {lane.description}
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col gap-2 overflow-y-auto p-2 pt-0">
                {lane.cards.length === 0 ? (
                    <p className="text-muted-foreground py-6 text-center text-xs">
                        Nothing here.
                    </p>
                ) : (
                    lane.cards.map((card) => (
                        <OutreachCard
                            key={card.outreach_id}
                            card={card}
                            stage={lane.stage}
                        />
                    ))
                )}
            </CardContent>
        </Card>
    );
}

function OutreachCard({
    card,
    stage,
}: {
    readonly card: AutoCalloutBoardCard;
    readonly stage: string;
}) {
    const firstShift = card.shifts[0];
    const extraShiftCount = Math.max(0, card.shifts.length - 1);
    const shiftStart = firstShift
        ? new Date(firstShift.shift_start_time)
        : null;
    const updatedAt = new Date(card.updated_at);

    return (
        <Link
            to="/outreaches/$callout-id"
            params={{ 'callout-id': card.outreach_id }}
            className="bg-card hover:bg-accent flex flex-col gap-1.5 rounded-md border p-2.5 text-sm transition-colors"
        >
            <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate font-medium">
                    {firstShift?.patient_name ?? 'Unknown client'}
                    {extraShiftCount > 0 ? (
                        <span className="text-muted-foreground ml-1 text-xs font-normal">
                            +{extraShiftCount}
                        </span>
                    ) : null}
                </span>
                <div className="flex shrink-0 items-center gap-1.5">
                    {card.mode === 'sandbox' ? (
                        <Badge
                            variant="secondary"
                            className="uppercase tabular-nums"
                        >
                            Sandbox
                        </Badge>
                    ) : null}
                    {card.auto_start_outreach && !card.auto_drafted ? (
                        <Badge
                            variant="default"
                            className="uppercase tabular-nums"
                            title="Composer Auto-start opt-in"
                        >
                            Auto-start
                        </Badge>
                    ) : null}
                    {firstShift?.role_required ? (
                        <Badge
                            variant="outline"
                            className="uppercase tabular-nums"
                        >
                            {firstShift.role_required}
                        </Badge>
                    ) : null}
                </div>
            </div>
            <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
                <span className="min-w-0 truncate">
                    {card.organization_name}
                </span>
                <span className="shrink-0 tabular-nums">
                    {shiftStart ? format(shiftStart, 'EEE h:mma') : '—'}
                </span>
            </div>
            <div className="flex items-center justify-between gap-2">
                <CountTrail
                    stage={stage}
                    suggested={card.suggested_caregiver_count}
                    reached={card.contact_attempt_count}
                    replied={card.responded_count}
                />
                <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                    {formatDistanceToNowStrict(updatedAt, { addSuffix: true })}
                </span>
            </div>
        </Link>
    );
}

// Per-stage metric visibility — show only what's meaningful at this stage.
// Drafting always has 0/0/0 (nothing dispatched yet) — show nothing.
// Awaiting start may have suggestions but never contact attempts.
// Reaching out can have both. Resolved + paused/cancelled show whatever
// the outreach reached when it stopped.
const STAGE_METRICS: Record<
    string,
    ReadonlyArray<'suggested' | 'reached' | 'replied'>
> = {
    drafting: [],
    awaiting_start: ['suggested'],
    reaching_out: ['suggested', 'reached'],
    resolved: ['suggested', 'reached', 'replied'],
    paused_or_cancelled: ['suggested', 'reached', 'replied'],
};

function CountTrail({
    stage,
    suggested,
    reached,
    replied,
}: {
    readonly stage: string;
    readonly suggested: number;
    readonly reached: number;
    readonly replied: number;
}) {
    const metrics = STAGE_METRICS[stage] ?? ['suggested', 'reached', 'replied'];
    if (metrics.length === 0) {
        return null;
    }
    const valueFor = (key: 'suggested' | 'reached' | 'replied') =>
        key === 'suggested' ? suggested : key === 'reached' ? reached : replied;
    return (
        <div className="flex items-center gap-1.5 text-xs tabular-nums">
            {metrics.map((m, i) => (
                <Fragment key={m}>
                    {i > 0 ? <CountArrow /> : null}
                    <CountTerm value={valueFor(m)} label={m} />
                </Fragment>
            ))}
        </div>
    );
}

function CountTerm({
    value,
    label,
}: {
    readonly value: number;
    readonly label: string;
}) {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <Badge variant="secondary" className="tabular-nums">
                    {value}
                </Badge>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
                {value} {label}
            </TooltipContent>
        </Tooltip>
    );
}

function CountArrow() {
    return (
        <span aria-hidden={true} className="text-muted-foreground/60 text-xs">
            →
        </span>
    );
}

export default AutoOutreachBoardPage;
